/**
 * THE TWO PURE PIECES EVERY REVIEWS LIST SHARES: the fold a search term goes
 * through before it is compared, and the comparison itself.
 *
 * These were written inside `ReviewsModal` for the PRODUCT reviews list. The
 * shop's own reviews — the ones transcribed from Google and the ones left
 * through Stylon — now get the same search, and a second copy of a
 * diacritic-folding table is a second copy to fall out of date: the Arabic
 * normalisation below is the difference between a shopper finding
 * "الرائحة" and not, and it must mean the same thing on both lists.
 *
 * `ReviewsModal` re-exports `normalizeForSearch` so the import path that
 * existed before this split still answers.
 */

export function normalizeForSearch(value) {
  return String(value ?? '')
    .normalize('NFD')
    // Combining marks (Latin accents) and the Arabic/Hebrew diacritic blocks.
    .replace(/[̀-ͯؐ-ًؚ-ٰٟۖ-ۭ֑-ׇ]/g, '')
    // Alef with hamza/madda → bare alef; teh marbuta → heh; alef maqsura → yeh.
    .replace(/[آأإٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .toLowerCase();
}

/**
 * Does this haystack match the query? Every word must appear somewhere in
 * the joined, folded parts — the same "all words, any order" rule the
 * product list has always used.
 *
 * The caller passes the PARTS, not the row, because the two lists keep their
 * text in different columns (`body` + the displayed reviewer here, `text` +
 * `authorName` there) and, more importantly, because what goes in the
 * haystack is a privacy decision the caller owns: the product list
 * deliberately leaves the customer's full name out, so that typing a surname
 * cannot confirm a surname the page withheld.
 */
export function textMatches(parts, query) {
  const q = normalizeForSearch(query).trim();
  if (!q) return true;
  const list = Array.isArray(parts) ? parts : [parts];
  const hay = normalizeForSearch(list.filter(Boolean).join(' '));
  return q.split(/\s+/).every((word) => hay.includes(word));
}
